import React from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider as _QueryClientProvider,
} from "@tanstack/react-query";
import { useLogout } from "./hooks/useLogout";
import toast from "react-hot-toast";
import { ResponseError } from "@norma-bi/bi-api";
import { throttle } from "@norma-bi/utils";
import { useCookies } from "react-cookie";

import { authenticationCacheScope } from "./Auth/authenticationCacheScope";

const MAX_RETRIES = 3;

export function QueryClientProvider(
  props: React.PropsWithChildren<{
    retries?: boolean;
    /**
     * When true skipTokenCheck disables the automatic logout mechanism that triggers when the token cookie is missing.
     * Primarily used in test environments to prevent unexpected logouts during tests
     * In the normal flow, this should always be false
     */
    skipTokenCheck?: boolean;
  }>,
) {
  const { retries = true, skipTokenCheck = false } = props;
  const [cookies] = useCookies(["token"]);

  const cacheScope = authenticationCacheScope(cookies.token);
  const cacheOwner = React.useMemo(() => ({ scope: cacheScope }), [cacheScope]);

  const activeOwner = React.useRef(cacheOwner);
  React.useLayoutEffect(() => {
    activeOwner.current = cacheOwner;
  }, [cacheOwner]);

  const [logoutOwner, setLogoutOwner] = React.useState<typeof cacheOwner>();

  const showUnknownErrorToast = React.useMemo(
    () =>
      throttle(() => {
        toast.error("Something went wrong, please try again later");
      }, 1000),
    [],
  );

  const onError = React.useCallback(
    (err: Error) => {
      showUnknownErrorToast();

      if (err instanceof ResponseError) {
        console.error("Response error: ", err.response);
      }
    },
    [showUnknownErrorToast],
  );

  const queryClient = React.useMemo(() => {
    // A different signed-in user must never inherit self or business queries.
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: (numRetries, err) => {
            if (activeOwner.current !== cacheOwner) return false;
            const respErr = err as Partial<{ response: { status: number } }>;
            if (respErr?.response?.status === 401) {
              setLogoutOwner(cacheOwner);
              return false;
            }
            if (respErr?.response?.status === 403) {
              return false;
            }
            return retries && numRetries < MAX_RETRIES;
          },
        },
      },
      queryCache: new QueryCache({
        onError: (err, query) => {
          if (activeOwner.current !== cacheOwner) return;
          if (query.meta?.preventDefaultErrorHandling) {
            return;
          }
          return onError(err);
        },
      }),
      mutationCache: new MutationCache({
        onError: (err, _vars, _ctx, mutation) => {
          if (activeOwner.current !== cacheOwner) return;
          if (mutation.meta?.preventDefaultErrorHandling) {
            return;
          }
          return onError(err);
        },
      }),
    });
  }, [onError, retries, cacheOwner]);

  React.useEffect(() => {
    if (skipTokenCheck) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && !cookies.token) {
        setLogoutOwner(cacheOwner);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [skipTokenCheck, cookies.token, cacheOwner]);

  return (
    <_QueryClientProvider key={cacheScope} client={queryClient}>
      <LogoutHandler logout={logoutOwner === cacheOwner}>{props.children}</LogoutHandler>
    </_QueryClientProvider>
  );
}

function LogoutHandler(props: React.PropsWithChildren<{ logout: boolean }>) {
  const { logout } = useLogout();

  React.useEffect(() => {
    if (props.logout) {
      logout();
    }
  }, [props.logout, logout]);

  return props.children;
}
